/**
 * The add-players drawer's filtered-results body (SE-6953 PC7, reworked by
 * SE-7090/SE-7094, result model clarified by QA 2026-08-17).
 *
 * Shown after the filters panel commits: the tab strip is hidden and the
 * drawer renders the filtered result set with a summary row ("N RESULTS · N
 * FILTERS APPLIED" + CLEAR ALL) and the applied filters as removable chips.
 *
 * ## Where the results come from — the QA-clarified model
 *
 * Pietra's acceptance ruling on the SE-7090 fix: with plain advanced filters
 * the drawer must return the SAME result set as the Search Player modal —
 * "the search is still not across all players" — while the LISTS scoping
 * stays pool-side. So the three dimensions resolve as:
 *
 * - **Advanced groups only** (Basic · Financial · Fit & Ratings ·
 *   Performance · Goalkeeper): the global `filterPlayers` read the Search
 *   Player modal runs (`usePlayerSearchPagination` + `useFilterVariables`),
 *   in the modal's order, paginated by scroll — PLUS any drawer-pool member
 *   that matches the filters but is missing from the global index (e.g. a
 *   manually-created player), appended after the server rows so modal
 *   parity is preserved. The pool matches come from
 *   `useAddPlayersFilteredPool` + `matchesAdvancedFilters`.
 * - **The LISTS group** (SE-7094) is watchlist membership, which only pool
 *   rows carry — so any lists selection scopes the whole result set to the
 *   pool union (advanced groups then narrow it client-side). QA-approved as
 *   shipped.
 * - **The REASON group** exists only on suggestion candidates and narrows to
 *   the matching candidates, as before.
 *
 * A reason-only selection needs no reads at all — it is answered entirely by
 * the suggestions in hand.
 *
 * Removing a chip re-runs the results by construction — chips mutate the
 * committed `filterState` (or the reason/lists selections), and everything
 * below is derived from those.
 * @module features/player-kanban/components/organisms/AddPlayersFilteredResults
 */

import React, { useCallback, useMemo } from "react";
import type { NativeScrollEvent, NativeSyntheticEvent } from "react-native";

import type {
  PlayerAdvancedFilterInput,
  SuggestionReasonCategory,
} from "@/generated/graphql";
import type { FilterState } from "@/hooks/useFilterState";
import type { FilterOption } from "@/features/scouting/components/molecules/FilterChip";
import { useAuth } from "@/features/auth";
import { DEFAULT_SORT_ORDER } from "@/features/scouting/constants/sortOptions";
import useFilterVariables from "@/features/scouting/hooks/useFilterVariables";
import deriveActiveFilterChips from "@/features/scouting/utils/deriveActiveFilterChips";
import buildChipRemoveHandlers from "@/components/organisms/shared/search-modal/utils/buildChipRemoveHandlers";
import { usePlayerSearchPagination } from "@/hooks/usePlayerSearchPagination";

import { SUGGESTION_REASON_COPY } from "../../../constants/suggestionReasons";
import type { SuggestedCandidate } from "../../../hooks/useSuggestedPlayers";
import useAddPlayersFilteredPool from "../../../hooks/useAddPlayersFilteredPool";
import type { ServerGpvRange } from "../../../utils/playerFilter";
import {
  filterByReasons,
  type ReasonSelection,
} from "../../../utils/addPlayersDrawerControls";
import {
  matchesAdvancedFilters,
  matchesListSelection,
} from "../../../utils/addPlayersFilterPredicate";
import {
  formatCandidateGpr,
  formatCandidateMeta,
  formatCandidateName,
} from "../../../utils/addPlayersRowMeta";

import AddPlayersFilteredResultsView, {
  type FilteredResultRow,
} from "./AddPlayersFilteredResultsView";

/** Chip-key prefix for the REASON chips, disjoint from the advanced keys. */
const REASON_CHIP_PREFIX = "reason_";

/** Chip-key prefix for the LISTS chips (SE-7094), same disjointness. */
const LIST_CHIP_PREFIX = "list_";

/** Pixels from the bottom of the scroll that trigger the next server page. */
const SCROLL_THRESHOLD = 200;

/** Props for the AddPlayersFilteredResults container. */
export interface AddPlayersFilteredResultsContainerProps {
  /** The drawer's committed filter state, owned by the panel container. */
  readonly filterState: FilterState;
  /** The committed REASON selection, owned by the panel container. */
  readonly reasonSelection: ReasonSelection;
  /** The committed LISTS selection — watchlist ids (SE-7094). */
  readonly listSelection: readonly string[];
  /** LISTS options, for the chips' display names (SE-7094). */
  readonly listOptions: readonly FilterOption[];
  /** Remove one committed reason category (chip ×). */
  readonly onRemoveReason: (category: SuggestionReasonCategory) => void;
  /** Remove one committed list id (chip ×, SE-7094). */
  readonly onRemoveList: (listId: string) => void;
  /** Remove EVERY applied filter, restoring the tabbed state. */
  readonly onClearAll: () => void;
  /** The loaded suggestion candidates — the Suggested slice of the union. */
  readonly candidates: readonly SuggestedCandidate[];
  /** Every org watchlist id — the Watchlists pool, same set as the tab. */
  readonly allWatchlistIds: readonly string[];
  /** Active board cost-filter GPV bounds, mirrored from the tab (SE-6496). */
  readonly serverGpvRange?: ServerGpvRange | null;
  /** Ids of players already on this pipeline — their rows mute as "In list". */
  readonly pipelinePlayerIds: ReadonlySet<string>;
  /** Player ids with an add in flight. */
  readonly addingPlayerIds: readonly string[];
  /** Add a player to the pipeline. */
  readonly onAdd: (playerId: string) => void;
}

/**
 * Drive the filtered-results body.
 * @param root0 - Component props.
 * @param root0.filterState - Committed filter state.
 * @param root0.reasonSelection - Committed REASON selection.
 * @param root0.listSelection - Committed LISTS selection (SE-7094).
 * @param root0.listOptions - LISTS options for chip labels (SE-7094).
 * @param root0.onRemoveReason - Per-reason chip removal.
 * @param root0.onRemoveList - Per-list chip removal (SE-7094).
 * @param root0.onClearAll - Remove every applied filter.
 * @param root0.candidates - Loaded suggestion candidates.
 * @param root0.allWatchlistIds - Every org watchlist id.
 * @param root0.serverGpvRange - Active GPV bounds (SE-6496).
 * @param root0.pipelinePlayerIds - Players already on this pipeline.
 * @param root0.addingPlayerIds - Ids with an add in flight.
 * @param root0.onAdd - Add callback.
 */
const AddPlayersFilteredResultsContainer = ({
  filterState,
  reasonSelection,
  listSelection,
  listOptions,
  onRemoveReason,
  onRemoveList,
  onClearAll,
  candidates,
  allWatchlistIds,
  serverGpvRange = null,
  pipelinePlayerIds,
  addingPlayerIds,
  onAdd,
}: AddPlayersFilteredResultsContainerProps) => {
  const auth = useAuth();
  const hasAuth = Boolean(auth?.data?.accessToken);

  const hasAdvancedFilters = filterState.activeFilterCount > 0;
  const hasListFilters = listSelection.length > 0;

  /*
   * Advanced-only runs the GLOBAL read for Search Player modal parity; a
   * lists selection scopes everything to the pools instead, so the global
   * read stays skipped for it.
   */
  const isGlobalSearch = hasAdvancedFilters && !hasListFilters;

  /*
   * The committed advanced groups as the SAME input object the server path
   * builds — untouched groups are absent keys, so activity semantics are
   * inherited, not re-derived.
   */
  const advancedInput = useFilterVariables(
    filterState,
    ""
  ) as PlayerAdvancedFilterInput;

  const pagination = usePlayerSearchPagination({
    isOpen: isGlobalSearch,
    hasAuth,
    debouncedSearch: "",
    sortBy: null,
    sortOrder: DEFAULT_SORT_ORDER,
    filterVariables: advancedInput,
    hasFilters: isGlobalSearch,
    includeTotalCount: true,
  });

  /*
   * The pool union backs BOTH non-reason modes: it IS the result set when a
   * lists selection scopes to the pools, and it supplies the appended
   * pool-only extras (players missing from the global index) when the global
   * read owns the results.
   */
  const pool = useAddPlayersFilteredPool({
    isActive: hasAdvancedFilters || hasListFilters,
    watchlistIds: allWatchlistIds,
    serverGpvRange,
    candidates,
  });

  /** Suggestion candidates matching the reason selection, keyed by player. */
  const reasonMatchedByPlayerId = useMemo(() => {
    const matched = filterByReasons(candidates, reasonSelection);
    return new Map(matched.map(candidate => [candidate.player.id, candidate]));
  }, [candidates, reasonSelection]);

  /** Every candidate by player id, for enriching server rows with reasons. */
  const candidatesByPlayerId = useMemo(
    () =>
      new Map(candidates.map(candidate => [candidate.player.id, candidate])),
    [candidates]
  );

  /** Whether a row passes the REASON narrowing (no selection = pass). */
  const passesReasons = useCallback(
    (playerId: string) =>
      reasonSelection.length === 0 || reasonMatchedByPlayerId.has(playerId),
    [reasonMatchedByPlayerId, reasonSelection.length]
  );

  /** The pool union narrowed by every committed dimension. */
  const poolMatches = useMemo(
    () =>
      pool.rows
        .filter(row => matchesAdvancedFilters(row.player, advancedInput))
        .filter(row => matchesListSelection(row.player, listSelection))
        .filter(row => passesReasons(row.player.id)),
    [advancedInput, listSelection, passesReasons, pool.rows]
  );

  const rows = useMemo<readonly FilteredResultRow[]>(() => {
    if (!hasAdvancedFilters && !hasListFilters) {
      /* Reason-only: the matching suggestion cards, in bestRank order. */
      return candidates
        .filter(candidate => reasonMatchedByPlayerId.has(candidate.player.id))
        .map(candidate => ({
          playerId: candidate.player.id,
          name: formatCandidateName(candidate.player),
          meta: formatCandidateMeta(candidate.player),
          gpr: formatCandidateGpr(candidate.player),
          reasons: candidate.reasons,
          isInList: pipelinePlayerIds.has(candidate.player.id),
        }));
    }

    const poolRows = poolMatches.map(row => ({
      playerId: row.player.id,
      name: formatCandidateName(row.player),
      meta: formatCandidateMeta(row.player),
      gpr: formatCandidateGpr(row.player),
      reasons: row.reasons,
      isInList: pipelinePlayerIds.has(row.player.id),
    }));

    if (!isGlobalSearch) {
      /* Lists-scoped: the pool union IS the result set (QA-approved). */
      return poolRows;
    }

    /* Global mode: the server rows in the modal's own order, each enriched
     * with reason chips when the player is also a suggestion candidate, then
     * the pool-only extras the global index missed. */
    const serverRows = pagination.players
      .filter(player => passesReasons(player.id))
      .map(player => ({
        playerId: player.id,
        name: formatCandidateName(player),
        meta: formatCandidateMeta(player),
        gpr: formatCandidateGpr(player),
        reasons: candidatesByPlayerId.get(player.id)?.reasons,
        isInList: pipelinePlayerIds.has(player.id),
      }));
    const serverIds = new Set(serverRows.map(row => row.playerId));
    return [
      ...serverRows,
      ...poolRows.filter(row => !serverIds.has(row.playerId)),
    ];
  }, [
    candidates,
    candidatesByPlayerId,
    hasAdvancedFilters,
    hasListFilters,
    isGlobalSearch,
    pagination.players,
    passesReasons,
    pipelinePlayerIds,
    poolMatches,
    reasonMatchedByPlayerId,
  ]);

  /*
   * The summary's N RESULTS. In global mode the server's totalCount is the
   * number the Search Player modal would state for the same filters — the
   * parity QA asked for — and the client-side narrowings (reasons) or the
   * fully-client modes fall back to the rows in hand.
   */
  const resultCount =
    isGlobalSearch && reasonSelection.length === 0
      ? (pagination.totalCount ?? rows.length)
      : rows.length;

  const filtersAppliedCount =
    filterState.activeFilterCount +
    reasonSelection.length +
    listSelection.length;

  /** List display names by id, for the LISTS chips (SE-7094). */
  const listLabelById = useMemo(
    () => new Map(listOptions.map(option => [option.id, option.label])),
    [listOptions]
  );

  /** Applied-filter chips: reasons, lists, then the advanced descriptors. */
  const chips = useMemo(() => {
    const reasonChips = reasonSelection.map(category => ({
      key: `${REASON_CHIP_PREFIX}${category}`,
      label: SUGGESTION_REASON_COPY[category]?.label ?? String(category),
    }));
    const listChips = listSelection.map(listId => ({
      key: `${LIST_CHIP_PREFIX}${listId}`,
      label: listLabelById.get(listId) ?? "List",
    }));
    return [
      ...reasonChips,
      ...listChips,
      ...deriveActiveFilterChips(filterState),
    ];
  }, [filterState, listLabelById, listSelection, reasonSelection]);

  const chipRemoveHandlers = useMemo(() => {
    const advanced = buildChipRemoveHandlers(filterState) as Record<
      string,
      () => void
    >;
    const withReasons = reasonSelection.reduce(
      (acc, category) => ({
        ...acc,
        [`${REASON_CHIP_PREFIX}${category}`]: () => onRemoveReason(category),
      }),
      advanced
    );
    return listSelection.reduce(
      (acc, listId) => ({
        ...acc,
        [`${LIST_CHIP_PREFIX}${listId}`]: () => onRemoveList(listId),
      }),
      withReasons
    );
  }, [
    filterState,
    listSelection,
    onRemoveList,
    onRemoveReason,
    reasonSelection,
  ]);

  const { hasMore, loadMore } = pagination;
  const canLoadMore = isGlobalSearch && hasMore;

  const handleScroll = useCallback(
    (event: NativeSyntheticEvent<NativeScrollEvent>) => {
      const { layoutMeasurement, contentOffset, contentSize } =
        event.nativeEvent;
      const distanceFromBottom =
        contentSize.height - layoutMeasurement.height - contentOffset.y;
      if (distanceFromBottom < SCROLL_THRESHOLD) loadMore();
    },
    [loadMore]
  );

  const isLoading = isGlobalSearch ? pagination.isLoading : pool.isLoading;
  const isError = isGlobalSearch
    ? pagination.isError || pool.isError
    : pool.isError;
  const error = isError
    ? "Something went wrong loading results. Try adjusting the filters."
    : null;

  return (
    <AddPlayersFilteredResultsView
      rows={rows}
      resultCount={resultCount}
      filtersAppliedCount={filtersAppliedCount}
      chips={chips}
      chipRemoveHandlers={chipRemoveHandlers}
      onClearAll={onClearAll}
      isLoading={isLoading}
      error={error}
      addingPlayerIds={addingPlayerIds}
      onAdd={onAdd}
      onScroll={canLoadMore ? handleScroll : undefined}
    />
  );
};

AddPlayersFilteredResultsContainer.displayName =
  "AddPlayersFilteredResultsContainer";

export default AddPlayersFilteredResultsContainer;
